import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import { api } from '../services/api';
import { formatMetricaDate } from '../utils/dateUtils';
import { formatMoneyPlain } from '../utils/formatMoney';
import { parseVendorsResponse, parseProjectTagsResponse } from '../utils/stringComboboxUtils';
import { downloadExpenseImportTemplate } from '../utils/expenseImportTemplate';
import { parseExpenseImportFile } from '../utils/expenseImportParse';
import { ValidImportRow, SkippedImportRow, IMPORT_BATCH_SIZE } from '../utils/expenseImport';
import editorStyles from './ExpenseEditorPage.module.css';
import styles from './ExpenseImportPage.module.css';

const ExpenseImportPage: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const { state } = useApp();
  const { showError, showSuccess, showConfirmation, setLoading } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [fileError, setFileError] = useState('');
  const [validRows, setValidRows] = useState<ValidImportRow[]>([]);
  const [skippedRows, setSkippedRows] = useState<SkippedImportRow[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [result, setResult] = useState<{ created: number; skipped: number } | null>(null);

  const tree = state.categoryTree;
  const fileDate = formatMetricaDate(new Date().toISOString()) || 'today';

  const handleDownload = async () => {
    if (!tree || !(tree.categories || []).length) {
      showError('אין עץ קטגוריות. יש להגדיר קטגוריות לפני הייבוא.');
      return;
    }
    try {
      let vendors: string[] = [];
      let projectTags: string[] = [];
      try {
        vendors = parseVendorsResponse(await api.expenses.getVendors(undefined, 100));
      } catch {
        vendors = [];
      }
      try {
        projectTags = parseProjectTagsResponse(await api.expenses.getProjectTags(undefined, 100));
      } catch {
        projectTags = [];
      }
      await downloadExpenseImportTemplate(tree, fileDate, vendors, projectTags);
    } catch (error) {
      showError('שגיאה בהורדת התבנית', { action: 'Download import template', error });
    }
  };

  const resetPreview = () => {
    setValidRows([]);
    setSkippedRows([]);
    setResult(null);
    setFileError('');
  };

  const handleFile = async (file: File | undefined) => {
    resetPreview();
    if (!file) return;
    setFileName(file.name);
    setIsParsing(true);
    try {
      const parsed = await parseExpenseImportFile(file, tree);
      if (parsed.error) {
        setFileError(parsed.error);
        return;
      }
      setValidRows(parsed.valid);
      setSkippedRows(parsed.skipped);
    } catch (error) {
      showError('שגיאה בקריאת הקובץ', { action: 'Parse import file', error });
    } finally {
      setIsParsing(false);
    }
  };

  const handleImport = async () => {
    if (!validRows.length) return;
    const confirmed = await showConfirmation({
      title: 'ייבוא הוצאות',
      message: `לייבא ${validRows.length} הוצאות תקינות? שורות עם שגיאה לא ייובאו.`,
      confirmText: 'ייבוא',
      cancelText: 'ביטול',
      type: 'info',
    });
    if (confirmed !== 'confirm') return;

    setIsImporting(true);
    setLoading(true, 'import-expenses');
    try {
      let created = 0;
      const skippedFromServer: SkippedImportRow[] = [];
      for (let i = 0; i < validRows.length; i += IMPORT_BATCH_SIZE) {
        const batch = validRows.slice(i, i + IMPORT_BATCH_SIZE);
        const response = await api.expenses.importRows(
          batch.map((row) => ({
            row: row.row,
            vendor_name: row.vendorName,
            net_amount: row.netAmount,
            vat_amount: row.vatAmount,
            total_amount: row.totalAmount,
            category_1: row.category1,
            category_2: row.category2,
            category_3: row.category3,
            receipt_date: row.documentDate,
            invoice_number: row.invoiceNumber,
            project_tag: row.projectTag,
            notes: row.notes,
            document_type: row.documentType,
          }))
        );
        if (!response.success || !response.data) {
          showError(response.error || 'הייבוא נכשל', { action: 'Import expenses', error: response.error });
          return;
        }
        created += response.data.createdCount ?? response.data.created?.length ?? 0;
        skippedFromServer.push(
          ...(response.data.skipped || []).map((item) => ({
            row: item.row,
            reason: item.reason,
            kind: item.kind === 'duplicate' ? 'duplicate' as const : 'invalid' as const,
            existingId: item.existingId,
          }))
        );
      }
      setSkippedRows((prev) => [...prev, ...skippedFromServer]);
      setResult({ created, skipped: skippedFromServer.length + skippedRows.length });
      setValidRows([]);
      showSuccess(`יובאו ${created} הוצאות`);
    } catch (error) {
      showError('שגיאה בייבוא ההוצאות', { action: 'Import expenses', error });
    } finally {
      setIsImporting(false);
      setLoading(false, 'import-expenses');
    }
  };

  return (
    <div className={embedded ? styles.embedded : styles.page}>
      <header className={styles.header}>
        {!embedded ? (
          <div>
            <h1>ייבוא הוצאות</h1>
            <p className={styles.lead}>הורידו את התבנית, החליפו את שורת הדוגמה, והעלו את הקובץ.</p>
          </div>
        ) : null}
        <div className={styles.actions}>
          <button type="button" className={editorStyles.saveButton} onClick={handleDownload}>
            הורדת תבנית
          </button>
          <button
            type="button"
            className={styles.fileButton}
            onClick={() => fileRef.current?.click()}
            disabled={isParsing || isImporting}
          >
            {fileName ? 'החלפת קובץ' : 'העלאת קובץ'}
          </button>
          <input
            ref={fileRef}
            className={styles.hiddenInput}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(event) => handleFile(event.target.files?.[0])}
          />
        </div>
      </header>

      {fileError ? <p className={styles.fileError}>{fileError}</p> : null}

      {(validRows.length > 0 || skippedRows.length > 0) && (
        <section className={styles.panel}>
          <div className={styles.summary}>
            <span className={styles.validCount}>{validRows.length} שורות תקינות</span>
            <span className={styles.errorCount}>{skippedRows.length} שורות עם שגיאה</span>
            {fileName ? <span>{fileName}</span> : null}
          </div>

          {validRows.length > 0 ? (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>שורה</th>
                    <th>שם ספק</th>
                    <th>סכום</th>
                    <th>L1</th>
                    <th>תאריך</th>
                  </tr>
                </thead>
                <tbody>
                  {validRows.slice(0, 50).map((row) => (
                    <tr key={row.row}>
                      <td>{row.row}</td>
                      <td>{row.vendorName}</td>
                      <td>{formatMoneyPlain(row.totalAmount)}</td>
                      <td>{row.category1}</td>
                      <td className={styles.dateCell}>{formatMetricaDate(row.documentDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={styles.empty}>אין שורות תקינות לייבוא.</p>
          )}

          {skippedRows.length > 0 ? (
            <div className={styles.tableWrap} style={{ marginTop: 16 }}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>שורה</th>
                    <th>שגיאה</th>
                  </tr>
                </thead>
                <tbody>
                  {skippedRows.map((row, index) => (
                    <tr key={`${row.row}-${index}`}>
                      <td>{row.row}</td>
                      <td>{row.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <div className={editorStyles.actionButtons}>
            <button
              type="button"
              className={editorStyles.saveButton}
              onClick={handleImport}
              disabled={!validRows.length || isImporting}
            >
              {isImporting ? 'מייבא...' : `ייבוא ${validRows.length} הוצאות`}
            </button>
            <button type="button" className={styles.secondaryButton} onClick={resetPreview}>
              ניקוי
            </button>
          </div>
        </section>
      )}

      {result ? (
        <p className={styles.result}>
          יובאו {result.created} הוצאות.
          {result.skipped ? ` ${result.skipped} שורות דולגו.` : ''}{' '}
          <Link to="/">למעבר לרשימת ההוצאות</Link>
        </p>
      ) : null}
    </div>
  );
};

export default ExpenseImportPage;
